import React, { createContext, useContext, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import useLocalStorage from './useLocalStorage';
import { getInitials, handleError } from '../utils';

const AuthContext = createContext({} as any);
export const baseUrl = process.env.API_BASE_URL;
const instance = axios.create({
  baseURL: baseUrl,
  headers: {
    'Content-Type': 'application/json',
    timeout: 1000,
  },
  withCredentials: true,
  // .. other options
});
const AuthProvider = ({ children }: any) => {
  const [user, setUser] = useLocalStorage('user', null);

  const navigate = useNavigate();
  const location = useLocation();
  const toastBottom = useRef<any>(null);
  // systemRoles: brand_owner, agency, reader, reviewer, marcom, admin

  // workflowRoles: legal_reviewer, medical_reviewer and regulatory_reviewer
  const permissions = {
    admin: [
      'menu_dashboard',
      'menu_aiplayground',
      'menu_workflows',
      'menu_assets',
      'menu_helpcenter',
      'create_workflow',
      'delete_asset',
      'change_annotation_status',
      'view_verdicts',
      'override_verdict',
      'cancel_workflow',
      'delete_workflow',
      'manage_users',
      'menu_profile',
      'manage_global_colors',
      'manage_brands',
      'manage_groups',
      'manage_delegations',
      'manage_reviewer_label',
      'annotation_file_action',
      'delete_feedback',
      'view_rules',
      'revert_version',
      'manage_due_date',
      'manage_asset',
      'config_sso',
      'menu_reports',
      'manage_agency',
      'manage_target_audience',
      'upload_final_version_with_changes'
    ],
    marcom: [
      'menu_dashboard',
      'menu_aiplayground',
      'menu_workflows',
      'menu_assets',
      'menu_helpcenter',
      'create_workflow',
      'delete_asset',
      'change_annotation_status',
      'view_verdicts',
      'override_verdict',
      'cancel_workflow',
      'delete_workflow',
      'manage_users',
      'menu_profile',
      'manage_global_colors',
      'manage_brands',
      'manage_groups',
      'manage_delegations',
      'manage_reviewer_label',
      'annotation_file_action',
      'delete_feedback',
      'view_rules',
      'revert_version',
      'manage_due_date',
      'manage_asset',
      'menu_reports',
      'manage_agency',
      'manage_target_audience',
      'upload_final_version_with_changes'
    ],
    reviewer: [
      'menu_dashboard',
      'menu_aiplayground',
      'menu_workflows',
      'menu_assets',
      'menu_helpcenter',
      'menu_profile',
      'view_verdicts',
      'comment_on_attachment_file',
      'add_annotation_on_attachment_file',
      'action_change_status',
      'tab_view_annotation',
      'manage_delegations',
      'annotation_file_action'
    ],
    reader: [
      'menu_dashboard',
      'menu_workflows',
      'menu_helpcenter',
      'menu_aiplayground',
      'menu_profile',
      'comment_on_attachment_file',
      'tab_view_annotation',
      'tab_final_upload',
      'manage_delegations'
    ],
    brand_owner: [
      'menu_dashboard',
      'menu_aiplayground',
      'menu_workflows',
      'menu_assets',
      'menu_helpcenter',
      'menu_profile',
      'create_workflow',
      'cancel_workflow',
      'view_verdicts',
      'upload_annotation_file',
      'comment_on_annotation_file',
      'comment_on_attachment_file',
      'add_annotation_on_annotation_file',
      'add_annotation_on_revised_attachment_file',
      'add_annotation_on_attachment_file',
      'upload_attachment_file',
      'upload_final_file',
      'action_send_for_approval',
      'tab_basic_info',
      'tab_metadata',
      'tab_upload_materials',
      'tab_view_annotation',
      'tab_final_upload',
      'change_annotation_status',
      'manage_delegations',
      'annotation_file_action',
      'revert_version',
      'upload_final_version_with_changes'
    ],
    agency: [
      'menu_dashboard',
      'menu_aiplayground',
      'menu_workflows',
      'menu_assets',
      'menu_helpcenter',
      'menu_profile',
      'create_workflow',
      'view_verdicts',
      'upload_annotation_file',
      'comment_on_annotation_file',
      'comment_on_attachment_file',
      'add_annotation_on_annotation_file',
      'add_annotation_on_revised_attachment_file',
      'add_annotation_on_attachment_file',
      'upload_attachment_file',
      'upload_final_file',
      'action_send_for_approval',
      'tab_basic_info',
      'tab_metadata',
      'tab_upload_materials',
      'tab_view_annotation',
      'tab_final_upload',
      'change_annotation_status',
      'manage_delegations',
      'annotation_file_action',
      'revert_version',
      'upload_final_version_with_changes'
    ]
  };

  /*
  const workflowPermissions = {
    brand_owner: [
      'upload_annotation_file',
      'comment_on_annotation_file',
      'comment_on_attachment_file',
      'add_annotation_on_annotation_file',
      'add_annotation_on_revised_attachment_file',
      'upload_attachment_file',
      'upload_final_file',
      'action_send_for_approval',
      'tab_basic_info',
      'tab_metadata',
      'tab_upload_materials',
      'tab_view_annotation',
      'tab_final_upload',
      'change_annotation_status'
    ],
    brand_reviewer: [
      'comment_on_attachment_file',
      'add_annotation_on_attachment_file',
      'action_change_status',
      'tab_view_annotation',
    ],
    workflow_reader: [
      'comment_on_attachment_file',
      'tab_view_annotation',
      'tab_final_upload'
    ]
  };
  */

  // call this function when you want to authenticate the user
  const login = async (data: any, locationSearch: string) => {
    const defaultLandingScreen = () => {
      localStorage.setItem('defaultLandingScreen', data?.landing_screen || 'dashboard');
      if (data?.landing_screen === 'workflow_list') {
        return '/workflows/list';
      } else if (data?.landing_screen === 'dashboard') {
        return '/home';
      }
      return '/home';
    };
    if (data.email) {
      localStorage.setItem('username', data.name);
      localStorage.setItem('currNameInitial', getInitials(data.name));
      localStorage.removeItem('logout-event');
      setUser(data);
      const params = new URLSearchParams(locationSearch);
      const redirectPath = params.get('redirect') || defaultLandingScreen();
      navigate(redirectPath, { replace: true });
    }
  };

  // call this function to sign out logged in user
  const logout = async (options?: { redirect?: string }) => {
    await instance.post('user-manager/logout').then((response: any) => {
      const { status } = response;

      if ([200, 201].includes(status)) {
        const respData = response.data.messages;
        if (respData) {
          setUser(null);
          localStorage.clear();
          sessionStorage.clear();
          try {
            localStorage.setItem('logout-event', Date.now().toString());
          } catch (e) {
            console.error('Error setting logout-event in localStorage:', e);
          }
          const redirectParam = options?.redirect ? `?redirect=${encodeURIComponent(options.redirect)}` : '';
          navigate(`/login${redirectParam}`, { replace: true });
        }
      }
    })
      .catch((error: any) => {
        if ([401, 404, 500].includes(error?.request?.status)) {
          handleError(error, toastBottom);
        }
      });
  };

  const getSourceType = () => {
    return localStorage.getItem('source');
  };
  const refreshToken = async (options?: { redirect?: string }) => {
    if (getSourceType() !== null) {
      await instance.post('user-manager/refresh_access_token', getSourceType(), { withCredentials: true }).then((response: any) => {
        const { status } = response;

        if ([200, 201].includes(status)) {
          // Token refreshed successfully
        }
      })
        .catch((error: any) => {
          if ([401, 404, 500].includes(error?.request?.status)) {
            handleError(error, toastBottom);
          }
        });
    }
  };

  const value = useMemo(
    () => ({
      user,
      login,
      logout,
      refreshToken,
      permissions,
      // workflowPermissions
    }),
    [user]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const useAuth = () => {
  return useContext(AuthContext);
};

export { AuthProvider, useAuth };
